// [목적] PlanItem CRUD mutation hooks — POST/PATCH/DELETE items 를 react-query useMutation 으로 래핑
// [이유] FlowLine A9(f0c27bb2): FolioEditPage 의 항목 고정(🔒)/삭제/직접 추가 UI 가 소비.
//        useDayPlans.ts(Day CRUD)와 동일 관례 — onSettled 에서 folio 캐시 invalidate.
// [사이드 이팩트] onSettled 에서 queryKeys.folio(folioId) invalidate → FolioEditPage/FolioPage 자동 갱신.
// [수정 시 고려사항] locked 토글은 patchPlanItem({ locked }) 로 처리 — useUpdateItem 재사용(별도 훅 불필요).

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateItemInput, PlanItem, UpdateItemInput } from '@gangwoncanvas/types';

import { createPlanItem, deletePlanItem, patchPlanItem } from '../api/folioClient.js';
import { queryKeys } from '../api/queryKeys.js';

interface CreateItemArgs {
  folioId: string;
  input: CreateItemInput;
}

interface UpdateItemArgs {
  folioId: string;
  itemId: string;
  input: UpdateItemInput;
}

interface DeleteItemArgs {
  folioId: string;
  itemId: string;
}

/**
 * [목적] PlanItem 생성 mutation — POST /folios/:id/items
 * [이유] A9: Day 편집 화면 "직접 추가" 폼에서 호출.
 * [사이드 이팩트] onSettled 에서 folio 캐시 invalidate.
 * [수정 시 고려사항] input.dayPlanId 필수(createItemSchema) — 호출부가 현재 선택된 Day 의 id 를 채워야 함.
 */
export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation<PlanItem, Error, CreateItemArgs>({
    mutationFn: ({ folioId, input }) => createPlanItem(folioId, input),
    onSettled: (_data, _err, { folioId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.folio(folioId) });
    },
  });
}

/**
 * [목적] PlanItem 부분 수정 mutation — PATCH /folios/:id/items/:itemId
 * [이유] A9: 항목 고정(locked) 토글 등 부분 필드 수정에 사용.
 * [사이드 이팩트] onSettled 에서 folio 캐시 invalidate.
 * [수정 시 고려사항] 낙관적 업데이트 없음 — 고정 토글처럼 즉시 반영이 필요하면 호출부에서 로컬 state 병행 고려.
 */
export function useUpdateItem() {
  const qc = useQueryClient();
  return useMutation<PlanItem, Error, UpdateItemArgs>({
    mutationFn: ({ folioId, itemId, input }) => patchPlanItem(folioId, itemId, input),
    onSettled: (_data, _err, { folioId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.folio(folioId) });
    },
  });
}

/**
 * [목적] PlanItem 삭제 mutation — DELETE /folios/:id/items/:itemId
 * [이유] A9: Day 편집 화면 항목 삭제 버튼.
 * [사이드 이팩트] onSettled 에서 folio 캐시 invalidate.
 * [수정 시 고려사항] 삭제 확인 다이얼로그는 호출부(FolioEditPage) 책임.
 */
export function useDeleteItem() {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteItemArgs>({
    mutationFn: ({ folioId, itemId }) => deletePlanItem(folioId, itemId),
    onSettled: (_data, _err, { folioId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.folio(folioId) });
    },
  });
}
